import PQueue from 'p-queue'
import pRetry from 'p-retry'
import { EventEmitter } from 'eventemitter3'
import type {
  SubscribeDevClientConfig,
  BalanceInfo,
  TransactionHistoryOptions,
  TransactionHistory,
  RateLimitInfo,
  Prediction,
  ProgressCallback,
  UserStorage,
  StorageOptions,
  SubscriptionStatus,
  MultimodalMessageContent,
  ChatMessage,
  ResponseFormat,
  SubscriptionPlansResponse,
  CheckoutSessionRequest,
  CheckoutSessionResponse,
  FreeSignupRequest,
  FreeSignupResponse,
  UsageLimits
} from './types'
import {
  SubscribeDevError,
  InsufficientBalanceError,
  RateLimitError,
  AuthenticationError,
  AccessDeniedError,
  NotFoundError,
  ValidationError,
  UnsubscribedError
} from './errors'

/**
 * SubscribeDevClient - Drop-in replacement for Replicate.js with billing and rate limiting
 */
export class SubscribeDevClient extends EventEmitter {
  private readonly config: SubscribeDevClientConfig & {
    baseUrl: string
    timeout: number
    maxRetries: number
    debug: boolean
  }
  private readonly queue: PQueue
  private readonly fetch: typeof fetch

  constructor(config: SubscribeDevClientConfig) {
    super()
    
    // Set defaults
    this.config = {
      baseUrl: config.baseUrl || 'https://subscribedev.volterapp-dev.com',
      timeout: config.timeout || 300000, // 5 minutes
      maxRetries: config.maxRetries || 2,
      debug: config.debug || false,
      ...config
    }

    // Create request queue to handle rate limiting
    this.queue = new PQueue({
      concurrency: 10, // Max concurrent requests
      interval: 60000, // 1 minute
      intervalCap: 60 // Max requests per minute
    })

    // Use global fetch or polyfill - bind to preserve 'this' context for browsers
    this.fetch = globalThis.fetch?.bind(globalThis)
    if (!this.fetch) {
      throw new Error('fetch is not available. Please provide a fetch polyfill.')
    }

    this.debug('SubscribeDevClient initialized', { baseUrl: this.config.baseUrl })
  }

  get pricingUrl() {
    return `${this.config.baseUrl}/subscribe`
  }

  /**
   * Run a prediction (main method matching Replicate.js API)
   * Note: Now returns completed predictions directly - no polling needed
   */
  async run(
    model: string,
    options: {
      input: Record<string, any>     // Can contain prompt OR messages array with multimodal content
      response_format?: ResponseFormat
      wait?: boolean                 // For compatibility - ignored since predictions complete synchronously
    }
  ): Promise<any> {
    const { input, response_format } = options

    this.debug('Starting prediction', { model, input, response_format })

    // Make direct API call and return output
    const requestPayload: any = {
      model,
      input: options.input
    }
    
    if (options.response_format) {
      requestPayload.response_format = options.response_format
    }
    
    const response = await this.makeRequest('POST', '/v1/run', requestPayload)

    this.debug('Prediction completed', { 
      hasOutput: !!response.output 
    })

    // Emit usage-changed event after successful run
    this.emit('usage-changed', { operation: 'run', model })

    // Return just the output from the API
    return response
  }

  /**
   * Create a new prediction
   */
  async createPrediction(
    model: string,
    options: { 
      input: Record<string, any>  // Supports multimodal messages array or simple prompt
      response_format?: ResponseFormat
    }
  ): Promise<Prediction> {
    return this.queue.add(async () => {
      return await pRetry(
        async () => {
          const requestPayload: any = {
            model,
            input: options.input
          }
          
          if (options.response_format) {
            requestPayload.response_format = options.response_format
          }
          
          const response = await this.makeRequest('POST', '/v1/run', requestPayload)

          // Emit usage-changed event after successful prediction creation
          this.emit('usage-changed', { operation: 'createPrediction', model })

          // API returns { output: ... }, create a synthetic prediction object
          const now = new Date().toISOString()
          return {
            id: `pred_${Math.random().toString(36).substring(2, 15)}`,
            created_at: now,
            started_at: now,
            completed_at: now,
            status: 'succeeded' as const,
            input: options.input,
            output: response.output,
            urls: {
              get: `${this.config.baseUrl}/v1/predictions/pred_${Math.random().toString(36).substring(2, 15)}`
            }
          }
        },
        {
          retries: this.config.maxRetries,
          onFailedAttempt: (error) => {
            this.debug('Prediction creation failed, retrying', { 
              attempt: error.attemptNumber,
              error: error.message 
            })
          },
          // @ts-ignore: Don't retry on UnsubscribedError
          shouldRetry: ((error: any) => {
            return !(error instanceof UnsubscribedError)
          }) as any,
          // Reduce retry delay to prevent timeouts
          factor: 1.5,
          minTimeout: 500,
          maxTimeout: 2000
        }
      )
    }) as Promise<Prediction>
  }

  /**
   * Get prediction status
   * @deprecated Since predictions now complete synchronously, this is mainly for historical lookups
   */
  async getPrediction(id: string): Promise<Prediction> {
    console.warn('[DEPRECATED] getPrediction() is deprecated. Predictions now complete synchronously in run().')
    return this.queue.add(async () => {
      const response = await this.makeRequest('GET', `/v1/predictions/${id}`)
      return this.transformResponse(response)
    }) as Promise<Prediction>
  }

  /**
   * Cancel a prediction
   */
  async cancelPrediction(id: string): Promise<Prediction> {
    return this.queue.add(async () => {
      const response = await this.makeRequest('POST', `/v1/predictions/${id}/cancel`)
      return this.transformResponse(response)
    }) as Promise<Prediction>
  }

  /**
   * Wait for prediction completion with optional progress callback
   * @deprecated Since predictions now complete synchronously, this method is no longer needed
   */
  async waitForPrediction(
    id: string,
    progressCallback?: ProgressCallback
  ): Promise<Prediction> {
    console.warn('[DEPRECATED] waitForPrediction() is deprecated. Predictions now complete synchronously in run().')
    this.debug('Waiting for prediction completion', { id })

    let attempts = 0
    const maxAttempts = 1800 // 30 minutes with 1s intervals
    
    while (attempts < maxAttempts) {
      const prediction = await this.getPrediction(id)
      
      if (progressCallback) {
        progressCallback(prediction)
      }

      // Check if prediction is complete
      if (['succeeded', 'failed', 'canceled'].includes(prediction.status)) {
        this.debug('Prediction completed', { id, status: prediction.status })
        return prediction
      }

      // Wait before next poll (exponential backoff up to 5s)
      const delay = Math.min(1000 + (attempts * 100), 5000)
      await new Promise(resolve => setTimeout(resolve, delay))
      attempts++
    }

    throw new SubscribeDevError(
      'Prediction timeout - exceeded maximum wait time',
      'PREDICTION_TIMEOUT',
      408
    )
  }

  /**
   * Get account balance information
   */
  async getBalance(): Promise<BalanceInfo> {
    const response = await this.makeRequest('GET', '/v1/subscriptions/usage')
    const userBalance = response.credits?.userBalance
    return {
      allocatedCredits: userBalance?.allocatedCredits || 0,
      usedCredits: userBalance?.usedCredits || 0,
      remainingCredits: userBalance?.remainingCredits || 0
    }
  }

  /**
   * Get transaction history
   */
  async getTransactions(options: TransactionHistoryOptions = {}): Promise<TransactionHistory> {
    const params = new URLSearchParams()
    
    if (options.limit) params.set('limit', options.limit.toString())
    if (options.offset) params.set('offset', options.offset.toString())
    if (options.status) params.set('status', options.status)
    if (options.model) params.set('model', options.model)
    if (options.startDate) params.set('start_date', options.startDate)
    if (options.endDate) params.set('end_date', options.endDate)

    const url = `/v1/transactions${params.toString() ? '?' + params.toString() : ''}`
    return this.makeRequest('GET', url)
  }

  /**
   * Get rate limit information
   */
  async getRateLimits(): Promise<RateLimitInfo> {
    return this.makeRequest('GET', '/v1/rate-limits')
  }

  /**
   * Get user storage data using auth context
   */
  async getStorage(options: StorageOptions = {}): Promise<UserStorage> {
    const params = new URLSearchParams()
    
    if (options.appVersion) {
      params.set('appVersion', options.appVersion)
    }

    const url = `/v1/storage${params.toString() ? '?' + params.toString() : ''}`
    return this.makeRequest('GET', url)
  }

  /**
   * Update user storage data using auth context
   */
  async setStorage(
    sessionData: Record<string, any>, 
    options: StorageOptions = {}
  ): Promise<UserStorage> {
    const params = new URLSearchParams()
    
    if (options.appVersion) {
      params.set('appVersion', options.appVersion)
    }

    const url = `/v1/storage${params.toString() ? '?' + params.toString() : ''}`
    return this.makeRequest('PUT', url, { sessionData })
  }

  /**
   * Delete user storage data using auth context
   */
  async deleteStorage(options: StorageOptions = {}): Promise<void> {
    const params = new URLSearchParams()
    
    if (options.appVersion) {
      params.set('appVersion', options.appVersion)
    }

    const url = `/v1/storage${params.toString() ? '?' + params.toString() : ''}`
    await this.makeRequest('DELETE', url)
  }

  /**
   * Browser convenience method: Persist session data locally and sync to server
   * Automatically saves the entire session object to localStorage and server
   */
  async persistSessionData(options: StorageOptions = {}): Promise<UserStorage> {
    if (typeof globalThis !== 'undefined' && typeof (globalThis as any).localStorage === 'undefined') {
      throw new Error('persistSessionData is only available in browser environments')
    }

    // Get current session data from server
    const serverData = await this.getStorage(options)
    
    // Store in localStorage for offline access
    const storageKey = `subscribeDev_session_${this.config.apiKey.slice(-8)}`
    ;(globalThis as any).localStorage.setItem(storageKey, JSON.stringify(serverData.sessionData))
    
    return serverData
  }

  /**
   * Browser convenience method: Get session data from server and update localStorage
   * Always fetches from server to ensure data is synchronized
   */
  async getSessionData(options: StorageOptions = {}): Promise<Record<string, any>> {
    // Always fetch from server to ensure synchronization
    const serverData = await this.getStorage(options)
    const sessionData = serverData.sessionData || {}
    
    // Update localStorage cache if in browser environment
    if (typeof globalThis !== 'undefined' && typeof (globalThis as any).localStorage !== 'undefined') {
      const storageKey = `subscribeDev_session_${this.config.apiKey.slice(-8)}`
      ;(globalThis as any).localStorage.setItem(storageKey, JSON.stringify(sessionData))
    }
    
    return sessionData
  }

  /**
   * Get user's subscription status using userKey authentication
   * Requires userKey to be provided in client configuration
   */
  async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    console.log('[SubscribeDevClient] getSubscriptionStatus called')
    
    if (!this.config.userKey) {
      console.log('[SubscribeDevClient] No userKey provided, throwing ValidationError')
      throw new ValidationError(
        'User key is required to get subscription status. Provide userKey in client configuration.',
        { method: 'getSubscriptionStatus' }
      )
    }

    console.log('[SubscribeDevClient] Making request to /v1/subscriptions/plan with userKey:', this.config.userKey.substring(0, 10) + '...')

    try {
      const response = await this.makeRequest('GET', '/v1/subscriptions/plan')
      console.log('[SubscribeDevClient] Received response:', {
        hasUserPlan: !!response.userPlan,
        planId: response.userPlan?.planId,
        status: response.userPlan?.status,
        hasPlanDetails: !!response.userPlan?.plan
      })
      
      const userPlan = response.userPlan
      
      // Handle case where user has no subscription (status: 'none')
      if (!userPlan || userPlan.status === 'none' || !userPlan.plan) {
        console.log('[SubscribeDevClient] User has no active subscription, returning none status')
        return {
          hasActiveSubscription: false,
          status: userPlan?.status || 'none'
        }
      }

      const result = {
        hasActiveSubscription: userPlan.status === 'active',
        plan: userPlan.plan ? {
          id: userPlan.plan.id || userPlan.planId,
          name: userPlan.plan.name,
          price: userPlan.plan.price,
          tokenLimit: userPlan.plan.tokenLimit || userPlan.plan.token_limit,
          subtitle: userPlan.plan.subtitle,
          description: userPlan.plan.description,
          features: userPlan.plan.features
        } : undefined,
        status: userPlan.status,
        startedAt: userPlan.startedAt,
        endsAt: userPlan.endsAt
      }

      console.log('[SubscribeDevClient] Returning subscription status:', {
        hasActiveSubscription: result.hasActiveSubscription,
        status: result.status,
        planName: result.plan?.name
      })

      return result
    } catch (error: any) {
      console.log('[SubscribeDevClient] Error in getSubscriptionStatus:', {
        errorType: error.constructor.name,
        message: error.message
      })
      
      // Re-throw any genuine errors
      throw error
    }
  }

  /**
   * Get available subscription plans for the project
   */
  async getSubscriptionPlans(): Promise<SubscriptionPlansResponse> {
    return this.makeRequest('GET', '/v1/subscriptions/status')
  }

  /**
   * Create a checkout session for subscription payment
   * Requires userKey to be provided in client configuration
   */
  async createCheckoutSession(request: CheckoutSessionRequest): Promise<CheckoutSessionResponse> {
    if (!this.config.userKey) {
      throw new ValidationError(
        'User key is required to create checkout session. Provide userKey in client configuration.',
        { method: 'createCheckoutSession' }
      )
    }
    return this.makeRequest('POST', '/v1/subscriptions/checkout', request)
  }

  /**
   * Sign up for the free plan
   * Requires userKey to be provided in client configuration
   */
  async signupForFree(request: FreeSignupRequest): Promise<FreeSignupResponse> {
    if (!this.config.userKey) {
      throw new ValidationError(
        'User key is required to sign up for free plan. Provide userKey in client configuration.',
        { method: 'signupForFree' }
      )
    }
    return this.makeRequest('POST', '/v1/subscriptions/free-signup', request)
  }

  /**
   * Cancel user subscription
   * Requires userKey to be provided in client configuration
   */
  async cancelSubscription(userId: string, cancelAtPeriodEnd: boolean = true): Promise<{ success: boolean; cancelledAt: string }> {
    if (!this.config.userKey) {
      throw new ValidationError(
        'User key is required to cancel subscription. Provide userKey in client configuration.',
        { method: 'cancelSubscription' }
      )
    }
    return this.makeRequest('POST', `/v1/subscriptions/cancel/${userId}`, { cancelAtPeriodEnd })
  }

  /**
   * Get comprehensive usage limits and current consumption
   * Requires userKey to be provided in client configuration
   */
  async getUsageLimits(): Promise<UsageLimits> {
    if (!this.config.userKey) {
      throw new ValidationError(
        'User key is required to get usage limits. Provide userKey in client configuration.',
        { method: 'getUsageLimits' }
      )
    }
    return this.makeRequest('GET', '/v1/subscriptions/usage')
  }

  /**
   * Get comprehensive usage information including credits, rate limits, and consumption
   * Alias for getUsageLimits() - Requires userKey to be provided in client configuration
   */
  async getUsage(): Promise<UsageLimits> {
    return this.getUsageLimits()
  }

  /**
   * Make authenticated HTTP request to SubscribeDev API
   */
  private async makeRequest(
    method: string,
    path: string,
    body?: any
  ): Promise<any> {
    const url = `${this.config.baseUrl}${path}`
    
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${this.config.apiKey}`,
      'Content-Type': 'application/json',
      'User-Agent': `@subscribe.dev/client/0.1.0`
    }

    // Add user token if provided
    if (this.config.userKey) {
      headers['X-User-Token'] = this.config.userKey
    }

    const requestOptions: RequestInit = {
      method,
      headers,
      signal: AbortSignal.timeout(this.config.timeout)
    }

    if (body && ['POST', 'PUT', 'PATCH'].includes(method)) {
      requestOptions.body = JSON.stringify(body)
    }

    this.debug('Making request', { method, url, headers: { ...headers, Authorization: '[redacted]' } })

    const response = await this.fetch(url, requestOptions)
    
    // Handle response
    const responseText = await response.text()
    let responseData: any
    
    try {
      responseData = responseText ? JSON.parse(responseText) : null
    } catch {
      responseData = { error: { message: responseText } }
    }

    if (!response.ok) {
      this.handleErrorResponse(response, responseData)
    }

    this.debug('Request successful', { status: response.status, data: responseData })
    return responseData
  }

  /**
   * Handle error responses and throw appropriate error types
   */
  private handleErrorResponse(response: Response, data: any): never {
    const requestId = response.headers.get('x-request-id') || undefined
    const error = data?.error || {}
    const message = error.message || `HTTP ${response.status}`

    switch (response.status) {
      case 400:
        throw new ValidationError(message, error.details, requestId)
      case 401:
        throw new AuthenticationError(message, requestId)
      case 402:
        // Check if this is an unsubscribed error vs insufficient balance
        if (error.code === 'USER_UNSUBSCRIBED' || message.toLowerCase().includes('unsubscribed') || message.toLowerCase().includes('subscription')) {
          throw new UnsubscribedError(message, requestId)
        }
        throw new InsufficientBalanceError(
          message,
          error.details?.remainingCredits,
          error.details?.requiredCredits,
          requestId
        )
      case 403:
        throw new AccessDeniedError(message, requestId)
      case 404:
        throw new NotFoundError(message, requestId)
      case 429:
        throw new RateLimitError(
          message,
          error.details?.resetTime,
          error.details?.retryAfter,
          requestId
        )
      default:
        throw new SubscribeDevError(
          message,
          error.code || 'UNKNOWN_ERROR',
          response.status,
          error.details,
          requestId
        )
    }
  }

  /**
   * Transform API response to match Replicate.js format
   */
  private transformResponse(response: any): Prediction {
    return {
      id: response.id,
      version: response.version,
      created_at: response.created_at,
      started_at: response.started_at,
      completed_at: response.completed_at,
      status: response.status,
      input: response.input,
      output: response.output,
      error: response.error,
      logs: response.logs,
      metrics: response.metrics,
      // URLs are no longer included in completed predictions (use output field instead)
      urls: response.urls || undefined
    }
  }

  /**
   * Debug logging
   */
  private debug(message: string, data?: any) {
    if (this.config.debug) {
      console.log(`[SubscribeDevClient] ${message}`, data || '')
    }
  }


  /**
   * Static method to create client (for convenience)
   */
  static create(config: SubscribeDevClientConfig): SubscribeDevClient {
    return new SubscribeDevClient(config)
  }
}